import { useTranslations } from "next-intl";
import { Button } from "@chotca/ui";

// Shared explanation shown when a free/limited org hits its concurrent-OPEN
// posting cap. Spells out the three things users get confused about:
//   1. they've hit the cap,
//   2. the cap is on posts open AT ONCE (not total posts ever),
//   3. how to fix it — close a post, or upgrade to Pro.
// Reused by the upgrade modal (early block) and any inline/toast surface so the
// wording stays in one place.
export function PostingLimitReachedContent({
  limit,
  planName,
  nextPlanLabel,
  onUpgrade,
  onClose,
}: {
  limit: number;
  planName: string;
  // Marketing name of the next tier up (e.g. "Pro", "Business"). null when the
  // org is already on the top tier → no upgrade prompt, only the close hint.
  nextPlanLabel: string | null;
  // Omit when the plan choices are already rendered nearby (e.g. inside the
  // upgrade modal) so we don't show a redundant CTA.
  onUpgrade?: () => void;
  onClose?: () => void;
}) {
  const t = useTranslations("postings");
  const canUpgrade = nextPlanLabel != null;
  return (
    <div className="space-y-3">
      <p className="text-title-md font-extrabold text-ink">
        {t("quotaAtLimitTitle", { limit })}
      </p>
      <ul className="space-y-2 text-body-sm text-ash">
        <li className="flex gap-2">
          <span aria-hidden>•</span>
          <span>{t("quotaAtLimitPointReach", { limit, planName })}</span>
        </li>
        <li className="flex gap-2">
          <span aria-hidden>•</span>
          <span className="font-semibold text-ink">
            {t("quotaAtLimitPointActive")}
          </span>
        </li>
        <li className="flex gap-2">
          <span aria-hidden>•</span>
          <span>
            {canUpgrade
              ? t("quotaAtLimitPointGuide", { nextPlan: nextPlanLabel })
              : t("quotaAtLimitPointGuideMax")}
          </span>
        </li>
      </ul>
      {(onUpgrade && canUpgrade) || onClose ? (
        <div className="flex flex-col gap-2 pt-1 sm:flex-row">
          {onUpgrade && canUpgrade ? (
            <Button className="flex-1" onClick={onUpgrade}>
              {t("quotaAtLimitUpgradeCta", { nextPlan: nextPlanLabel })}
            </Button>
          ) : null}
          {onClose ? (
            <Button variant="ghost" className="flex-1" onClick={onClose}>
              {t("quotaAtLimitCloseCta")}
            </Button>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
